import { WithSelect } from '@components/design';
import { actions, overlays } from '@ecosystem/newron-design-system';
import React, { useEffect, useMemo, useState } from 'react';

import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import {
  Country,
  CountryFilterPair,
  FilterKeys,
  FilterKeyTitleAndExclusion,
  Filters,
  FilterTitle,
  MultiCountryObject,
  MultiCountryOptions,
  Option,
  PageType,
  SelectedFilters,
} from '@utils/pageConfig/Types';

import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
  isRetailInsightsOrMarketPremiumSubscription,
  isRetailKadSubscription,
  ProductType,
} from '@pages/product-analyzer/Product';
import { BaseTimePeriod, MainFilters } from '@services/FiltersTypes';
import { actionCreatorsProducts, actionCreatorsSideFilters } from '@state/Index';
import { MainFilterStateProducts } from '@state/reducers/main-filter-products/MainFiltersProductsReducer';
import { convertToCountrySelectorOptions, getPgsByCountries } from '@utils/CountryUtils';
import {
  getCurrencyBySelectedCountry,
  getUpdateMainFilters,
  isAllowSearchBox,
  isMainFiltersStatePeriodEmpty,
  updateAppliedCurrencyFilterByCountry,
  updateAppliedFilters,
} from '@utils/FilterUtils';
import { dispatchUpdateMainFiltersPeriod } from '@utils/PeriodUtils';
import { GrowthTypes } from '../comparison-growth-selector/Types';
import { RelativePeriod } from '../comparison-period-selector/Types';
import FilterSelector from '../filter-component/FilterSelector';
import { Periodicity } from '../time-period-selector/PeriodsTypes';
import { CountrySelectorOption, ProductGroupSelectorOption } from './Types';
import { MainFiltersState } from '@state/reducers/main-filter/MainFiltersReducer';
import { IRootState } from '@state/reducers/Reducers';
import { isEmptyString } from '@utils/StringUtils';
import { EMPTY_OPTION } from '@utils/Constants';
import { getCarryOverAllFiltersForApply, getExcludedPagesToCarryOver } from '@utils/filterTrayUtils/FilterTrayUtils';
import { cloneDeep } from 'lodash';

const GERMANY: Option = {
  label: 'Germany',
  value: 'DE',
};

const INITIAL_SELECTED_COUNTRY: CountrySelectorOption = EMPTY_OPTION;
const INITIAL_SELECTED_PRODUCT_GROUP: ProductGroupSelectorOption = EMPTY_OPTION;

type ChosenTimeFrameAndTimePeriod = {
  chosenTimeFrame: Periodicity;
  chosenTimePeriod: BaseTimePeriod;
};

interface ProductGroupModalProps {
  rawProductGroupResponse: CountryFilterPair[];
  rawProductGroupResponseProduct: CountryFilterPair[];
  productGroups: Option[];
  countries: Country[];
  onApply: (selectedProductGroup: Option, selectedCountry: Option[]) => void;
  isOpen: boolean;
  onClose: () => void;
  setProductGroup: (productGroup: Option) => void;
  initialFilters: MainFilters[];
  initialFiltersProducts: MainFilters[];
  isDisabled: boolean;
  productType: ProductType;
  periodDispatchActions?: {
    updatePeriod: (period: BaseTimePeriod) => void;
    updatePeriodicity: (periodicity: Periodicity) => void;
    updateComparisonPeriod: (comparisonPeriod: RelativePeriod | BaseTimePeriod) => void;
  };
  isMultiCountry?: boolean;
}

const ProductGroupModal: React.FC<ProductGroupModalProps> = ({
  rawProductGroupResponse,
  rawProductGroupResponseProduct,
  productGroups,
  countries,
  onApply,
  isOpen,
  onClose,
  setProductGroup,
  initialFilters,
  initialFiltersProducts,
  isDisabled,
  productType,
  periodDispatchActions,
  isMultiCountry,
}) => {
  const dispatch = useDispatch();
  const pagesConfig = usePageConfig();

  const { updateSideFilters } = bindActionCreators(actionCreatorsSideFilters, dispatch);
  const { updateMainFiltersProducts } = bindActionCreators(actionCreatorsProducts, dispatch);
  const mainFiltersState: MainFiltersState = useSelector((state: IRootState) => state.mainFilters);

  const [selectedCountry, setSelectedCountry] = useState<CountrySelectorOption>(INITIAL_SELECTED_COUNTRY);
  const [updatedCountry, setUpdatedCountry] = useState<string>(INITIAL_SELECTED_COUNTRY.value);
  const [selectedMultiCountries, setSelectedMultiCountries] = useState<MultiCountryOptions[]>([]);
  const [selectedProductGroup, setSelectedProductGroup] =
    useState<ProductGroupSelectorOption>(INITIAL_SELECTED_PRODUCT_GROUP);
  const [updatedProductGroup, setUpdatedProductGroup] = useState<string>(INITIAL_SELECTED_PRODUCT_GROUP.value);

  const countryKeyAndTitle: FilterKeyTitleAndExclusion = {
    filterKey: FilterKeys.COUNTRY,
    filterTitle: FilterTitle.COUNTRY,
  };

  const getFoundCountries = (pgResponse: CountryFilterPair[]) => {
    return pgResponse
      .map((filter) => {
        return filter.filter.options.find((options) => options.value === selectedProductGroup.value)
          ? filter.country
          : null;
      })
      .filter((option) => option !== null);
  };

  const productGroupSelectorOptions: ProductGroupSelectorOption[] = useMemo(() => {
    const selectedCountries = isMultiCountry ? selectedMultiCountries : [selectedCountry as Option];
    const selectedCountryCodes = selectedCountries.map((country) => country.value);
    if (
      selectedCountryCodes.length === 0 ||
      // This checks for the initial value
      selectedCountryCodes.some((countryCode) => isEmptyString(countryCode))
    ) {
      return productGroups;
    }

    const countryFilterPairToSearch = isRetailInsightsOrMarketPremiumSubscription(productType)
      ? rawProductGroupResponse
      : rawProductGroupResponseProduct;
    const countryFilterPairs = countryFilterPairToSearch.filter((filter) =>
      selectedCountryCodes.includes(filter.country),
    );

    return getPgsByCountries(countryFilterPairs, selectedCountries);
  }, [selectedCountry, selectedMultiCountries, productGroups]);

  const countrySelectorOptions: CountrySelectorOption[] = useMemo(() => {
    if (isEmptyString(selectedProductGroup.value)) {
      return convertToCountrySelectorOptions(countries);
    }

    // Checks which other countries the selected PG exist in
    let foundCountries: (string | null)[];
    if (isRetailKadSubscription(productType)) {
      foundCountries = [
        ...getFoundCountries(rawProductGroupResponse),
        ...getFoundCountries(rawProductGroupResponseProduct),
      ];
    } else if (isRetailInsightsOrMarketPremiumSubscription(productType)) {
      foundCountries = getFoundCountries(rawProductGroupResponse);
    } else {
      foundCountries = getFoundCountries(rawProductGroupResponseProduct);
    }

    const matchedCountries = countries
      .map((option) => {
        return foundCountries.find((x) => x === option.id) ? option : null;
      })
      .filter((option) => option !== null);

    return convertToCountrySelectorOptions(matchedCountries);
  }, [selectedProductGroup, countries]);

  useEffect(() => {
    const selectedOption = productGroupSelectorOptions.find((o) => o.value === updatedProductGroup);
    if (selectedOption) {
      setProductGroup(selectedOption);
      setSelectedProductGroup(selectedOption);
    }
  }, [updatedProductGroup]);

  useEffect(() => {
    const selectedOption = countrySelectorOptions.find((o) => o.value === updatedCountry);
    if (selectedOption) {
      setSelectedCountry(selectedOption);
    }
  }, [updatedCountry]);

  const renderProductGroupSelector = () => {
    return (
      productGroupSelectorOptions && (
        <div data-testid="product-group-selector">
          <WithSelect
            key={`${productGroupSelectorOptions}-${updatedProductGroup}`} //NOSONAR typescript:S6551
            id="product-group-selector"
            name="product-group-selector"
            label="Product Group"
            combobox={isAllowSearchBox(productGroupSelectorOptions)}
            options={productGroupSelectorOptions}
            value={updatedProductGroup}
            placeholder="Select Product Group..."
            disabled={productGroups.length === 0}
            onChange={(pg) => {
              setUpdatedProductGroup(pg);
            }}
          />
        </div>
      )
    );
  };

  const renderCountrySelector = () => {
    return (
      countrySelectorOptions && (
        <div data-testid="country-selector">
          <WithSelect
            key={`${countrySelectorOptions}-${updatedCountry}`} //NOSONAR typescript:S6551
            id="country-selector"
            name="country-selector"
            label="Country"
            combobox={isAllowSearchBox(countrySelectorOptions)}
            value={updatedCountry}
            options={countrySelectorOptions}
            placeholder="Select Country..."
            onChange={(country) => {
              setUpdatedCountry(country);
            }}
          />
        </div>
      )
    );
  };

  const renderMultiCountrySelector = () => {
    return (
      countrySelectorOptions && (
        <div id="country-selector" data-testid="country-selector">
          <FilterSelector
            selectFilter={countryKeyAndTitle}
            placeholder={'Select Country...'}
            options={countrySelectorOptions}
            value={selectedMultiCountries}
            onChangeFunc={handleMultiCountryChange}
          />
        </div>
      )
    );
  };

  const handleMultiCountryChange = (countryObject: MultiCountryObject) => {
    setSelectedMultiCountries(countryObject.options);
  };

  const dispatchMainFiltersProducts = (toStoreProductsMainFilters: MainFilterStateProducts) => {
    if (!isEmptyString(toStoreProductsMainFilters.period.value)) {
      updateMainFiltersProducts(toStoreProductsMainFilters);
    }
  };

  const updateMainFiltersPeriod = (
    chosenValues: ChosenTimeFrameAndTimePeriod,
    chosenValuesProducts: ChosenTimeFrameAndTimePeriod,
  ) => {
    if (pagesConfig.pageType === PageType.OPPORTUNITY_REVIEW || pagesConfig.pageType === PageType.TRADING_ASSISTANT) {
      dispatchUpdateMainFiltersPeriod(periodDispatchActions, chosenValues); // Update redux -> MainFilters
    } else if (pagesConfig.pageType === PageType.RI_PRODUCT) {
      dispatchUpdateMainFiltersPeriod(periodDispatchActions, chosenValuesProducts); // Update redux -> MainFilters
    }
  };

  const addCurrencyFilterToSelectedFilters = (allFilters: SelectedFilters[], countryToUpdate: Filters) => {
    // Whenever we navigate to these 4 pages for the first time
    //Modal will popup, and we will initialize a default currency for PA page side filters.
    // Analysis and Products
    const pageStatesToAddDefaultCurrencyFilter = [
      PageType.OPPORTUNITY_REVIEW,
      PageType.RI_PRODUCT,
      PageType.TRADING_ASSISTANT,
      PageType.PERFORMANCE_ANALYSIS,
    ];
    if (
      !pagesConfig.isMultiCountryFeatureFlagPA ||
      !pageStatesToAddDefaultCurrencyFilter.includes(pagesConfig.pageType)
    ) {
      return;
    }

    const currentSelectedFilters = allFilters[PageType.PERFORMANCE_ANALYSIS].selectedFilters;

    const countryOption = countryToUpdate.options.length > 1 ? GERMANY : countryToUpdate.options[0];
    updateAppliedCurrencyFilterByCountry(pagesConfig, countryOption, currentSelectedFilters);
  };

  const handleApply = () => {
    onApply(selectedProductGroup, selectedMultiCountries.length > 0 ? selectedMultiCountries : [selectedCountry]);

    const allFilters = cloneDeep(pagesConfig.appliedFilters);
    let currentSelectedFilters = allFilters[pagesConfig.pageType].selectedFilters;

    const productGroupToUpdate: Filters = {
      dataType: 'PRODUCT GROUP',
      feature: 'PRODUCT GROUP',
      filterTitle: 'Product Group',
      filterKey: FilterKeys.PRODUCT_GROUP,
      options: [selectedProductGroup],
      disabled: false,
    };
    updateAppliedFilters(productGroupToUpdate, currentSelectedFilters);

    const countryToUpdate: Filters = {
      dataType: 'COUNTRY',
      feature: 'COUNTRY',
      filterTitle: 'Country',
      filterKey: FilterKeys.COUNTRY,
      options: selectedMultiCountries.length > 0 ? selectedMultiCountries : [selectedCountry],
      disabled: false,
    };
    updateAppliedFilters(countryToUpdate, currentSelectedFilters);

    const currencyToUpdate: Filters = getCurrencyBySelectedCountry(pagesConfig, selectedCountry);
    updateAppliedFilters(currencyToUpdate, currentSelectedFilters);

    // get chosen time frame for Performance Analyzer Pages
    const chosenValues = getUpdateMainFilters(
      rawProductGroupResponse,
      selectedCountry,
      selectedProductGroup,
      initialFilters,
    );

    // get chosen time frame for Product Analyzer Page
    const chosenValuesProducts = getUpdateMainFilters(
      rawProductGroupResponseProduct,
      selectedCountry,
      selectedProductGroup,
      initialFiltersProducts,
    );

    let toStoreProductsMainFilters = {
      timeFrame: chosenValuesProducts.chosenTimeFrame,
      period: chosenValuesProducts.chosenTimePeriod,
      comparisonPeriod: RelativePeriod.PreviousYearCurrentPeriod,
      selectedGrowthType: GrowthTypes.percentage, // default growth type
      country: selectedCountry.value,
    };

    const excludedPagesForCarryOverFilters = getExcludedPagesToCarryOver(pagesConfig);
    const carriedOverAllFilters = getCarryOverAllFiltersForApply(
      allFilters,
      pagesConfig.pageType,
      undefined,
      excludedPagesForCarryOverFilters,
    );
    addCurrencyFilterToSelectedFilters(carriedOverAllFilters, countryToUpdate);
    pagesConfig.setIsNavigateFromRS(true);
    pagesConfig.setSelectedFilters(carriedOverAllFilters);
    pagesConfig.setAppliedFilters(carriedOverAllFilters);
    pagesConfig.setIsFirstVisit(true);

    updateSideFilters({
      appliedFilters: cloneDeep(carriedOverAllFilters),
    });
    dispatchMainFiltersProducts(toStoreProductsMainFilters);

    //To retain period, periodicity and comparison period between Perf Pages
    //Check if MainFiltersState periods is not empty, then no need to update period
    isMainFiltersStatePeriodEmpty(mainFiltersState) && updateMainFiltersPeriod(chosenValues, chosenValuesProducts);
  };

  const isGoToMarketButtonDisabled = () => {
    const isNoSelectedCountry = isMultiCountry
      ? selectedMultiCountries.length === 0
      : isEmptyString(selectedCountry.value);

    return isNoSelectedCountry || isEmptyString(selectedProductGroup.value) || isDisabled;
  };

  const { Modal } = overlays;
  const { Button } = actions;

  return (
    <div className="productGroupModalWrapper">
      <Modal isOpen={isOpen} size="md" onClose={onClose}>
        <Modal.Header>Choose Market</Modal.Header>
        <Modal.Body overflow={false}>
          <div className={`grid auto-cols-auto grid-flow-row ${isMultiCountry ? 'gap-y-0' : 'gap-y-6'}`}>
            {isMultiCountry ? renderMultiCountrySelector() : renderCountrySelector()}
            {renderProductGroupSelector()}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={onClose} mode="secondary" testId="cancel-button">
            Cancel
          </Button>
          <Button testId="button-apply" disabled={isGoToMarketButtonDisabled()} onClick={handleApply}>
            Go to Market
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ProductGroupModal;
